import { AbstractUser } from "@kvix/shared";
import { Divider, Grid, makeStyles, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import queryString from "query-string";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import {
  isBasicInstructor,
  isInstructor,
  KvixUserContext,
} from "../../../../../contexts/user";
import { AccountActionButtons } from "./sections/AccountActionButtons";
import { AgeInput } from "./sections/basic/AgeInput";
import { AvatarPicker, dataURIToBlob } from "./sections/basic/AvatarPicker";
import { CountryInput } from "./sections/basic/CountryInput";
import { GenderInput } from "./sections/basic/GenderInput";
import { MarketingCheckbox } from "./sections/basic/MarketingCheckbox";
import { NameInput } from "./sections/basic/NameInputs";
import { ConditionalGrid } from "./sections/ConditionalGrid";
import { AddressInputs } from "./sections/instructor/AddressInputs";
import { IdentityNumberInput } from "./sections/instructor/IdentityNumberInput";
import { PhoneNumberInput } from "./sections/instructor/PhoneNumberInput";
import { BasicUserUpdate } from "./sections/interfaces/BasicUserUpdate";
import { InstructorUserUpdate } from "./sections/interfaces/InstructorUserUpdate";

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: "420px",
  },
  marginBottom: {
    marginBottom: "20px",
  },
  divider: {
    backgroundColor: "#fee9ee",
    marginBottom: "20px",
    marginTop: "8px",
  },
}));

export const ProfilePageAccount: React.FC = () => {
  const classes = useStyles();
  const { t, ready } = useTranslation(["pages", "date", "common"]);
  const [basicUserUpdate, setBasicUserUpdate] = useState<BasicUserUpdate>();
  const [instructorUserUpdate, setInstructorUserUpdate] =
    useState<InstructorUserUpdate>();
  const [marketing, setMarketing] = useState<boolean>();
  const [canSave, setCanSave] = useState<boolean>(false);
  const { user, actions } = useContext(KvixUserContext);
  const basicInstructor = isBasicInstructor(user);
  const instructor = isInstructor(user);
  const [warning, setWarning] = useState<string>();
  const [severity, setSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("info");
  const history = useHistory();
  const queryParams = queryString.parse(history.location.search);
  const initalBasicUserUpdate: Partial<BasicUserUpdate>[] = [];
  const initalInstructorUserUpdate: Partial<InstructorUserUpdate>[] = [];
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [disableAction, setDisableAction] = useState<boolean>(true);

  const setBasicUserData = (data: Partial<BasicUserUpdate>) => {
    if (!basicUserUpdate || Object.keys(basicUserUpdate)?.length === 0) {
      initalBasicUserUpdate.push(data);
    } else {
      setBasicUserUpdate({ ...basicUserUpdate, ...data });
    }
  };

  const setInstructorUserData = (data: Partial<InstructorUserUpdate>) => {
    if (
      !instructorUserUpdate ||
      Object.keys(instructorUserUpdate)?.length === 0
    ) {
      initalInstructorUserUpdate.push(data);
    } else {
      setInstructorUserUpdate({ ...instructorUserUpdate, ...data });
    }
  };

  const setRegistrationAlert = (
    warning: string,
    severity: "error" | "warning" | "info" | "success"
  ) => {
    setWarning(`pages:profile.account.warning.${warning}`);
    setSeverity(severity);
  };

  useEffect(() => {
    if (queryParams.completeRegistration) {
      setRegistrationAlert("completeRegistration", "info");
      setMarketing(true);
    }
  }, [queryParams]);

  const changeCheck = (
    user: AbstractUser,
    basic: BasicUserUpdate,
    marketing: boolean,
    instructor?: InstructorUserUpdate
  ) =>
    (user && basic
      ? basic.avatar !== user.imageUrl ||
        basic.country !== user.country ||
        basic.dateOfBirth?.toDateString() !==
          new Date(user.dateOfBirth).toDateString() ||
        basic.firstName !== user.name.first ||
        basic.lastName !== user.name.last ||
        basic.gender !== user.gender
      : false) ||
    (user && instructor
      ? instructor.addressLine1 !== user.address1 ||
        instructor.addressLine2 !== user.address2 ||
        instructor.city !== user.city ||
        instructor.zipcode !== user.zipcode ||
        instructor.identityNumber?.toString() !== user.identityNumber ||
        instructor.phoneNumber !== user.mobileNumber
      : false) ||
    (user && user.acceptAdvertisment !== marketing);

  useEffect(() => {
    if (basicUserUpdate) {
      const basicFields: boolean = Object.values(basicUserUpdate).every(
        (v) => !!v
      );

      if (basicInstructor || instructor) {
        const { addressLine2, identityNumber, phoneNumber, ...rest } = instructorUserUpdate;
        const instructorFields: boolean = Object.values(rest).every((v) => !!v);
        setCanSave(basicFields && instructorFields);
      } else if (basicFields) {
        setCanSave(true);
      } else setCanSave(false);
      setIsChanged(
        changeCheck(
          user,
          basicUserUpdate,
          marketing,
          basicInstructor || instructor ? instructorUserUpdate : undefined
        )
      );
    }
  }, [user, basicUserUpdate, instructorUserUpdate, marketing]);

  useEffect(() => {
    if (isChanged && canSave) {
      setDisableAction(false);
    } else {
      setDisableAction(true);
    }
  }, [isChanged, canSave]);

  const submit = () => {
    if (marketing !== user.acceptAdvertisment) {
      actions.updateAdvertisment(marketing);
    }

    const userInfo = {
      firstname: basicUserUpdate.firstName,
      lastname: basicUserUpdate.lastName,
      country: basicUserUpdate.country,
      gender: basicUserUpdate.gender,
      dateOfBirth: basicUserUpdate.dateOfBirth,
      ...(basicUserUpdate.avatar && {
        newAvatar:
          basicUserUpdate.avatar !== user.imageUrl
            ? dataURIToBlob(basicUserUpdate.avatar)
            : basicUserUpdate.avatar,
      }),
      ...((instructor || basicInstructor) && {
        address1: instructorUserUpdate.addressLine1,
        address2: instructorUserUpdate.addressLine2,
        zipcode: instructorUserUpdate.zipcode,
        city: instructorUserUpdate.city,
      }),
    };

    if (instructorUserUpdate && instructorUserUpdate.identityNumber) {
      userInfo['identityNumber'] = instructorUserUpdate.identityNumber?.toString();
    }

    if (instructorUserUpdate && instructorUserUpdate.phoneNumber) {
      userInfo['mobileNumber'] = instructorUserUpdate.phoneNumber;
    }

    actions
      .updateUserInfo(userInfo)
      .then(() => {
        setLoading(false);
        history.replace(history.location.pathname);
      });
  };

  useEffect(() => {
    if (initalBasicUserUpdate.length > 0) {
      let basic: Partial<BasicUserUpdate> = {};
      initalBasicUserUpdate.forEach((value) => {
        basic = { ...basic, ...value };
      });
      setBasicUserUpdate({ ...basicUserUpdate, ...basic });
    }

    if (initalInstructorUserUpdate.length > 0) {
      let instructor: Partial<InstructorUserUpdate> = {};
      initalInstructorUserUpdate.forEach((value) => {
        instructor = { ...instructor, ...value };
      });
      setInstructorUserUpdate({ ...instructorUserUpdate, ...instructor });
    }
  }, [initalBasicUserUpdate, initalInstructorUserUpdate]);

  if (!user || !ready) {
    return null;
  }

  const translations = {
    avatarPicker: {
      tooltip: t("pages:profile.account.avatarPicker.tooltip"),
    },
    nameInput: {
      firstNameField: t("pages:profile.account.nameInput.firstNameField"),
      lastNameField: t("pages:profile.account.nameInput.lastNameField"),
    },
    ageInput: {
      title: t("pages:profile.account.ageInput.title"),
      year: t("date:year"),
      month: t("date:month"),
      day: t("date:day"),
      months: {
        january: t("date:months.january.short"),
        february: t("date:months.february.short"),
        march: t("date:months.march.short"),
        april: t("date:months.april.short"),
        may: t("date:months.may.short"),
        june: t("date:months.june.short"),
        july: t("date:months.july.short"),
        august: t("date:months.august.short"),
        september: t("date:months.september.short"),
        october: t("date:months.october.short"),
        november: t("date:months.november.short"),
        december: t("date:months.december.short"),
      },
    },
    genderInput: {
      title: t("pages:profile.account.genderInput.title"),
      female: t("pages:profile.account.genderInput.female"),
      male: t("pages:profile.account.genderInput.male"),
      nonBinary: t("pages:profile.account.genderInput.nonBinary"),
    },
    countryInput: {
      title: t("pages:profile.account.countryInput.title"),
      label: t("pages:profile.account.countryInput.label"),
    },
    accountActionButtons: {
      save: t("common:save"),
    },
    marketingCheckbox: {
      title: t("pages:profile.account.marketingCheckbox.title"),
      body: t("pages:profile.account.marketingCheckbox.body"),
    },
    addressInputs: {
      title: t("pages:profile.account.addressInputs.title"),
      line1: t("pages:profile.account.addressInputs.line1"),
      line2: t("pages:profile.account.addressInputs.line2"),
      city: t("pages:profile.account.addressInputs.city"),
      zipcode: t("pages:profile.account.addressInputs.zipcode"),
    },
    identityNumberInput: {
      label: t("pages:profile.account.identityNumberInput.label"),
    },
    phoneNumerInput: {
      label: t("pages:profile.account.phoneNumerInput.label"),
    },
  };

  return (
    <Grid container direction="column" className={classes.container}>
      <Grid item>
        <Typography variant="h3">{t("pages:profile.account.title")}</Typography>
        <Typography variant="body1" className={classes.marginBottom}>
          {t("pages:profile.account.subtitle")}
        </Typography>
      </Grid>
      <ConditionalGrid condition={!!warning} className={classes.marginBottom}>
        <Alert
          variant="outlined"
          severity={severity}
          icon={severity === "info" && <>😊</>}
        >
          {t(warning)}
        </Alert>
      </ConditionalGrid>
      <AvatarPicker
        loading={loading}
        setBasicUserData={setBasicUserData}
        defaultAvatar={user.imageUrl}
        translations={translations.avatarPicker}
      />
      <Grid item>
        <NameInput
          loading={loading}
          defaultFirstName={user.name.first}
          defaultLastName={user.name.last}
          setBasicUserData={setBasicUserData}
          translations={translations.nameInput}
        />
      </Grid>
      <Grid item>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item>
        <AgeInput
          loading={loading}
          translations={translations.ageInput}
          defaultDate={new Date(user.dateOfBirth)}
          setBasicUserData={setBasicUserData}
        />
      </Grid>
      <Grid item>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item>
        <GenderInput
          loading={loading}
          defaultGender={user.gender}
          setBasicUserData={setBasicUserData}
          translations={translations.genderInput}
        />
      </Grid>
      <Grid item>
        <Divider className={classes.divider} />
      </Grid>
      <ConditionalGrid condition={basicInstructor || instructor}>
        <PhoneNumberInput
          loading={loading}
          translations={translations.phoneNumerInput}
          phoneNumber={user.mobileNumber}
          setInstructorUserData={setInstructorUserData}
        />
        <IdentityNumberInput
          loading={loading}
          translations={translations.identityNumberInput}
          identityNumber={Number.parseInt(user.identityNumber)}
          setInstructorUserData={setInstructorUserData}
        />
        <Grid item>
          <Divider className={classes.divider} />
        </Grid>
        <AddressInputs
          loading={loading}
          translations={translations.addressInputs}
          line1={user.address1}
          line2={user.address2}
          city={user.city}
          zipcode={user.zipcode}
          setInstructorUserData={setInstructorUserData}
        />
      </ConditionalGrid>
      <Grid item>
        <CountryInput
          loading={loading}
          defaultCountry={user.country}
          setBasicUserData={setBasicUserData}
          translations={translations.countryInput}
        />
      </Grid>
      <Grid item>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item>
        <MarketingCheckbox
          loading={loading}
          isChecked={queryParams.completeRegistration ? marketing : user.acceptAdvertisment}
          setMarketing={setMarketing}
          translations={translations.marketingCheckbox}
        />
      </Grid>
      <Grid item>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item>
        <AccountActionButtons
          submit={() => {
            if (!loading) {
              setLoading(true);
              submit();
            }
          }}
          translations={translations.accountActionButtons}
          disabled={disableAction}
          loading={loading}
        />
      </Grid>
    </Grid>
  );
};
